import { BASE_CRM_NODES } from './base-crm';
import { SALES_NODES } from './sales';
import { MARKETING_NODES } from './marketing';
import { PEVC_DEALFLOW_NODES } from './pevc-dealflow';
import { PEVC_PORTFOLIO_NODES } from './pevc-portfolio';
import { PEVC_FUNDRAISING_NODES } from './pevc-fundraising';
import { PEVC_IR_NODES } from './pevc-ir';
import { PMN_NODES } from './pmn';

export * from './base-crm';
export * from './sales';
export * from './marketing';
export * from './pevc-dealflow';
export * from './pevc-portfolio';
export * from './pevc-fundraising';
export * from './pevc-ir';
export * from './pmn';

/**
 * Central registry mapping node type to definition (schema + UI config)
 */
export const NODE_REGISTRY = {
  ...BASE_CRM_NODES,
  ...SALES_NODES,
  ...MARKETING_NODES,
  ...PEVC_DEALFLOW_NODES,
  ...PEVC_PORTFOLIO_NODES,
  ...PEVC_FUNDRAISING_NODES,
  ...PEVC_IR_NODES,
  ...PMN_NODES,
};

/**
 * Get node definition for a type
 */
export function getNodeDefinition(nodeType: string) {
  return NODE_REGISTRY[nodeType];
}

/**
 * Get schema for a node type
 */
export function getNodeSchema(nodeType: string) {
  return NODE_REGISTRY[nodeType]?.schema;
}

/**
 * Check if node type is valid
 */
export function isValidNodeType(nodeType: string): boolean {
  return nodeType in NODE_REGISTRY;
}

/**
 * Get all valid node types
 */
export function getAllNodeTypes(): string[] {
  return Object.keys(NODE_REGISTRY);
}

/**
 * Normalize a website URL for consistent deduplication.
 */
export function normalizeWebsite(url: string): string {
  return url
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
}

/**
 * Extract natural key from props for a given node type.
 */
export function extractNaturalKey(nodeType: string, props: Record<string, unknown>): string | undefined {
  const nodeDef = NODE_REGISTRY[nodeType];
  if (!nodeDef?.naturalKeyField) return undefined;

  const value = props[nodeDef.naturalKeyField];
  if (typeof value !== 'string' || value.trim().length === 0) return undefined;

  // Normalize website for Organization
  if (nodeDef.naturalKeyField === 'website') {
    return normalizeWebsite(value);
  }

  return value.trim();
}

/**
 * Field aliases for common user language variations.
 */
const FieldAliases: Record<string, Record<string, string>> = {
  Organization: {
    location: 'geo',
    address: 'geo',
    hq: 'geo',
    headquarters: 'geo',
    url: 'website',
    site: 'website',
    type: 'organization_type',
  },
  Person: {
    location: 'geo',
    address: 'geo',
  },
};

/**
 * Normalize a field name to its canonical schema name.
 */
export function normalizeFieldName(nodeType: string, field: string): string {
  const lowerField = field.toLowerCase();
  return FieldAliases[nodeType]?.[lowerField] ?? field;
}

/**
 * Normalize all field names in a props object.
 */
export function normalizeProps(nodeType: string, props: Record<string, unknown>): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    const normalizedKey = normalizeFieldName(nodeType, key);
    normalized[normalizedKey] = value;
  }
  return normalized;
}
